import { FreshnessNote } from "@/components/FreshnessNote";
import { freshnessDate } from "@/lib/currentYear";
import { useMemo } from "react";
import { Link, useNavigate } from "@/lib/router-compat";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEOHelmet } from "@/components/SEOHelmet";
import { PopularCardsSection } from "@/components/PopularCardsSection";
import { SegmentLinks } from "@/components/SegmentLinks";
import { useCards } from "@/hooks/useCards";
import { getSiteOrigin } from "@/lib/ssr-safe";
import { isRealCreditCard, type CardRow } from "@/config/segmentPages";

export interface KostenSearch {
  saldo: string;
}

export const defaultKostenSearch: KostenSearch = { saldo: "1500" };

const parseAmount = (value: string): number => {
  const normalized = value.replace(",", ".").replace(/[^0-9.]/g, "");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

const eur = (value: number) =>
  value.toLocaleString("de-DE", { style: "currency", currency: "EUR" });

interface CostRow {
  card: CardRow;
  annualFee: number;
  apr: number;
  interest: number;
  total: number;
}

interface KostenrechnerProps {
  values: KostenSearch;
  onChange: (key: keyof KostenSearch, value: string) => void;
}

export default function Kostenrechner({ values, onChange }: KostenrechnerProps) {
  const navigate = useNavigate();
  const { data: cards = [], isLoading } = useCards();
  const domain = getSiteOrigin();
  const pageUrl = `${domain}/tools/kostenrechner`;

  const balance = parseAmount(values.saldo);

  const rows = useMemo<CostRow[]>(() => {
    return (cards as CardRow[])
      .filter((card) => isRealCreditCard(card))
      .map((card) => {
        const annualFee = Number(card.annual_fee ?? 0);
        const apr = Number(card.apr_effective ?? 0);
        const interest = apr > 0 ? (balance * apr) / 100 : 0;
        return { card, annualFee, apr, interest, total: annualFee + interest };
      })
      .sort((a, b) => a.total - b.total);
  }, [cards, balance]);

  const handleNavigate = (page: string) => {
    if (page === "home") navigate("/");
  };

  const handleViewDetails = (slug: string) => navigate(`/kreditkarten/${slug}`);

  const schemas = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Startseite", item: domain },
        { "@type": "ListItem", position: 2, name: "Kreditkarten-Kostenrechner", item: pageUrl },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Kreditkarten-Kostenrechner",
      applicationCategory: "FinanceApplication",
      operatingSystem: "Web",
      url: pageUrl,
      offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex flex-col">
      <SEOHelmet
        title="Kreditkarten-Kostenrechner – Jahreskosten im Vergleich"
        description="Vergleichen Sie die jährlichen Kosten Ihrer Kreditkarte: Jahresgebühr plus Zinskosten bei Teilzahlung — sortiert nach den tatsächlichen Kosten pro Jahr."
        url={pageUrl}
        type="website"
        modifiedTime={freshnessDate()}
        additionalSchemas={schemas}
      />

      <Header onNavigate={handleNavigate} currentPage="tool-kostenrechner" />

      <main className="flex-1">
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Kreditkarten-Kostenrechner</h1>
          <FreshnessNote className="mb-3" label="Konditionen geprüft" />
          <p className="text-gray-600 mb-6">
            Was kostet eine Kreditkarte wirklich pro Jahr? Geben Sie an, welchen Betrag Sie im
            Schnitt über die Teilzahlung offen haben — wir rechnen Jahresgebühr plus Zinskosten für
            jede Karte im Vergleich.
          </p>

          <div className="rounded-lg border border-border bg-card p-5 shadow-sm mb-8">
            <label className="block max-w-xs">
              <span className="text-sm font-medium text-gray-900">
                Durchschnittlich offener Betrag (Teilzahlung)
              </span>
              <input
                type="text"
                inputMode="decimal"
                value={values.saldo}
                onChange={(e) => onChange("saldo", e.target.value)}
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                aria-describedby="hint-saldo"
              />
              <span id="hint-saldo" className="text-xs text-muted-foreground">
                0 eingeben, wenn Sie immer vollständig ausgleichen
              </span>
            </label>
          </div>

          <div aria-live="polite">
            {isLoading ? (
              <p className="text-gray-600">Karten werden geladen …</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/50 text-left">
                      <th className="px-4 py-3 font-semibold text-gray-900">Karte</th>
                      <th className="px-4 py-3 font-semibold text-gray-900 text-right">
                        Jahresgebühr
                      </th>
                      <th className="px-4 py-3 font-semibold text-gray-900 text-right">
                        Eff. Jahreszins
                      </th>
                      <th className="px-4 py-3 font-semibold text-gray-900 text-right">
                        Zinskosten/Jahr
                      </th>
                      <th className="px-4 py-3 font-semibold text-gray-900 text-right">
                        Kosten gesamt
                      </th>
                      <th className="px-4 py-3" aria-label="Aktion" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(({ card, annualFee, apr, interest, total }) => (
                      <tr key={card.id} className="border-b border-border last:border-0">
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">
                            {card.display_name || card.name}
                          </p>
                          <p className="text-xs text-muted-foreground">{card.issuer}</p>
                        </td>
                        <td className="px-4 py-3 text-right">{eur(annualFee)}</td>
                        <td className="px-4 py-3 text-right">
                          {apr > 0
                            ? `${apr.toLocaleString("de-DE", { maximumFractionDigits: 2 })} %`
                            : "—"}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {apr > 0 ? eur(interest) : "—"}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-900">
                          {eur(total)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Link
                            to={`/kreditkarten/${card.slug}`}
                            className="text-primary underline whitespace-nowrap"
                          >
                            Details
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <p className="text-xs text-muted-foreground mt-4">
              Berücksichtigt werden Jahresgebühr und Sollzinsen auf den angegebenen offenen Betrag.
              Gebühren für Fremdwährung und Bargeldabhebungen liegen uns derzeit nicht je Karte vor
              und sind nicht eingerechnet. Karten ohne bekannten effektiven Jahreszins zeigen nur
              die Jahresgebühr. Maßgeblich ist das Preis- und Leistungsverzeichnis des Anbieters.
              Keine Finanzberatung.
            </p>
          </div>

          <p className="text-gray-600 mt-8">
            Wie sich eine Rückzahlung über die Zeit entwickelt, zeigt der{" "}
            <Link to="/tools/teilzahlungsrechner" className="text-primary underline">
              Teilzahlungsrechner
            </Link>
            . Passende Karten zu Ihrer Situation finden Sie mit dem{" "}
            <Link to="/tools/kreditkarten-finder" className="text-primary underline">
              Kreditkarten-Finder
            </Link>
            .
          </p>
        </section>

        <SegmentLinks />

        {/* Gängige Kreditkarten – always shown directly above the footer */}
        <PopularCardsSection
          currentPage="tool-kostenrechner"
          forceVisible
          onViewDetails={handleViewDetails}
          onViewAllCards={() => navigate("/kreditkarten")}
        />
      </main>

      <Footer onNavigate={handleNavigate} />
    </div>
  );
}
